import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { DefaultButton, PrimaryButton } from 'office-ui-fabric-react';
import get from 'lodash/get';
import { fetchContractActivity, approve, reject } from '../../actions/contracts';
import TextButton from '../../components/TextButton';
import ContractTermsForm from '../../components/ContractTermsForm';
import Module from '../../components/Module';
import ModuleHeader from '../../components/ModuleHeader';
import ModuleContent from '../../components/ModuleContent';
import Main from '../../components/Main';
import PageTitle from '../../components/PageTitle';
import ContractActionRequired from '../../components/ContractActionRequired';
import SavingIndicator from '../../components/SavingIndicator';
import styles from './ReviewContractTermEdits.style';

const ReviewContractTermEdits = () => {
  const history = useHistory();
  const ref = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const { contractAddress } = useParams();
  const contract = useSelector(state => get(state, `entities.contracts.${contractAddress}`, {}));

  useEffect(() => {
    dispatch(fetchContractActivity(contractAddress));
  }, [dispatch, contractAddress]);

  // check contract approvalStatus to see if this page is even viewable.
  // If it's not pending approval then users should not be able to see it.
  useEffect(() => {
    if (contract.approvalStatus && contract.approvalStatus !== 'PendingApproval' && !isSubmitting) {
      history.push(`/contracts/${contractAddress}`);
    }
  }, [history, contract.approvalStatus, contractAddress, isSubmitting]);

  const handleCancel = () => {
    history.push(`/contracts/${contractAddress}`);
  };

  const handleReject = async () => {
    setIsSubmitting(true);
    const response = await dispatch(reject(contractAddress, true));

    if (!response.error) {
      history.push(`/contracts/${contractAddress}`);
    } else {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    const response = await dispatch(approve(contractAddress, true));

    if (!response.error) {
      history.push(`/contracts/${contractAddress}`);
    } else {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageTitle>Approve Contract Terms</PageTitle>

      <Main>
        <ContractActionRequired
          address={contractAddress}
          hideCta
          className={styles.actionRequired}
        />

        <Module>
          <ModuleHeader variant="xLarge">
            <span className={styles.moduleHeaderText}>Contract Terms</span>
          </ModuleHeader>

          <ModuleContent>
            <ContractTermsForm address={contractAddress} ref={ref} reviewMode />
          </ModuleContent>
        </Module>

        <div className={styles.buttonContainer}>
          <TextButton
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </TextButton>
          {!isSubmitting ? (
            <>
              <DefaultButton onClick={handleReject}>Reject</DefaultButton>
              <PrimaryButton onClick={handleApprove} disabled={isSubmitting}>
                Approve
              </PrimaryButton>
            </>
          ) : (
            <SavingIndicator className={styles.saving} />
          )}
        </div>
      </Main>
    </>
  );
};

export default ReviewContractTermEdits;
